import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GAME_TYPES } from '@/features/gameSession/store/constants';
import useBatteryLevel from '@/features/timeGame/hooks/useBatteryLevel';
import usePanelTransition from '@/features/timeGame/hooks/usePanelTransition';
import useTimeGameQuestion, { parseTimeString } from '@/features/timeGame/hooks/useTimeGameQuestion';
import useTimeGameResizeReloadKey from '@/features/timeGame/hooks/useTimeGameResizeReloadKey';
import useAudioPlayer from '@/shared/hooks/useAudioPlayer';
import { GameAudioProvider } from '@/shared/hooks/useGameAudioSettings';
import { EndOutcome } from '@/features/gameSession/types';
import { TimeQuestionType } from '@/features/timeGame/types';
import { getElapsedSeconds } from '@/shared/utils/time';

import EndScreen from '@/features/timeGame/components/stages/EndScreen';
import GameScreen from '@/features/timeGame/components/stages/GameScreen';
import StartScreen from '@/features/timeGame/components/stages/StartScreen';
import GameStyleBoundary from '@/shared/components/gameStyleBoundary/GameStyleBoundary';

import timeGameMusic from '@/features/timeGame/assets/sounds/music.ogg';

import '@/features/timeGame/styles/time-game.scss';

import type {
	GameAudioProps,
	GameAnswerEvent,
	GameCloseEvent,
	GameData,
	GameFinishEvent,
	Question,
} from '@/features/gameSession/types';

type SelectedTime = {
	hours: number;
	minutes: number;
};

const MAX_BATTERY_LEVEL = 5;
const MIN_BATTERY_LEVEL = 0;
const DEFAULT_SELECTED_TIME: SelectedTime = { hours: 0, minutes: 15 };

export type TimeGameProps = {
	gameData?: GameData | null;
	levelProgress?: any;
	questions?: Question[];
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onClose: (event?: GameCloseEvent) => void;
	onReplay?: (gameData: GameData) => void;
} & GameAudioProps;

const PANEL_TRANSITION_DURATION = 0.65;

const formatSelectedTime = ({ hours, minutes }: SelectedTime) => {
	const normalizedHours = ((hours % 24) + 24) % 24;

	return `${String(normalizedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const TimeGameContent = ({
	gameData: providedGameData,
	levelProgress,
	questions: providedQuestions,
	onAnswer,
	onClose,
	onReplay,
}: TimeGameProps) => {
	const questions = providedQuestions ?? [];
	const gameScreenResizeReloadKey = useTimeGameResizeReloadKey();
	const [hasGameStarted, setHasGameStarted] = useState<boolean>(false);
	const [endOutcome, setEndOutcome] = useState<EndOutcome | null>(null);
	const [answers, setAnswers] = useState<boolean[]>([]);
	const [questionQueue, setQuestionQueue] = useState<number[]>(() =>
		questions ? questions.map((_, index) => index) : [],
	);
	const [selectedTime, setSelectedTime] = useState<SelectedTime>(DEFAULT_SELECTED_TIME);
	// Track number of attempts per question (0 = first attempt, 1 = second attempt)
	const [questionAttempts, setQuestionAttempts] = useState<Record<number, number>>({});
	const questionStartedAtRef = useRef<number | null>(null);
	const { startPanelRef, gamePanelRef, transitionToGame, shouldRenderStartPanel } = usePanelTransition({
		duration: PANEL_TRANSITION_DURATION,
	});
	const { play: playBackgroundMusic, stop: stopBackgroundMusic } = useAudioPlayer({
		src: timeGameMusic,
		loop: true,
		volume: 0.3,
	});

	// Battery level management
	const { batteryLevel, increaseBattery, decreaseBattery } = useBatteryLevel();

	const totalQuestions = questions?.length || 0;
	const currentQuestionQueueIndex = questionQueue[0];
	const currentQuestion =
		currentQuestionQueueIndex != null && questions ? questions[currentQuestionQueueIndex] : undefined;
	const currentAlienPosition = questionQueue.length > 0 ? 0 : -1;
	const correctAnswersCount = answers.filter((a) => a).length;

	const starProgressData = useMemo(() => {
		if (!levelProgress) {
			return null;
		}

		const { level_completed, level_stars_earned, stars_earned: progressStarsEarned, current_level } = levelProgress;

		const totalStars = level_completed ? level_completed?.required_stars : current_level?.required_stars;

		const levelCompletedRequiredStars = level_completed?.required_stars;
		const rawEarned = level_stars_earned;
		const earnedStars =
			levelCompletedRequiredStars && rawEarned > levelCompletedRequiredStars
				? levelCompletedRequiredStars
				: rawEarned;

		const previousEarned = level_stars_earned - progressStarsEarned;

		return {
			progressStarsEarned,
			totalStars,
			earnedStars,
			previousEarned: Math.max(0, previousEarned),
		};
	}, [levelProgress]);

	useEffect(() => {
		if (!questions?.length || hasGameStarted) {
			return;
		}
		setQuestionQueue(questions.map((_, index) => index));
	}, [questions, hasGameStarted]);
	const needsDigitalAnswer = (currentQuestion?.type || '').includes('ToDigital');

	useEffect(() => {
		if (endOutcome !== null) {
			stopBackgroundMusic();
			return;
		}
		playBackgroundMusic();
		return () => {
			stopBackgroundMusic();
		};
	}, [endOutcome, playBackgroundMusic, stopBackgroundMusic]);

	const handleAnswerSubmitted = useCallback(
		(isCorrect: boolean, isSecondAttempt: boolean) => {
			setAnswers((prev) => [...prev, isCorrect]);

			const batteryDelta = isCorrect ? 1 : -1;
			const projectedBatteryLevel = Math.min(
				Math.max(batteryLevel + batteryDelta, MIN_BATTERY_LEVEL),
				MAX_BATTERY_LEVEL,
			);

			if (isCorrect) {
				increaseBattery();
			} else {
				decreaseBattery();
			}

			// Move to next question or end game after a brief delay
			setTimeout(() => {
				// Check if battery is depleted (game lost)
				if (projectedBatteryLevel <= MIN_BATTERY_LEVEL) {
					setEndOutcome(EndOutcome.LOST);
					return;
				}

				setQuestionQueue((prevQueue) => {
					if (prevQueue.length === 0) {
						return prevQueue;
					}

					const [currentIndex, ...rest] = prevQueue;
					let updatedQueue: number[];

					// Remove from queue if: correct OR second attempt (even if wrong)
					if (isCorrect || isSecondAttempt) {
						updatedQueue = rest;
					} else {
						// First attempt wrong: move to end of queue
						updatedQueue = rest.length > 0 ? [...rest, currentIndex] : [currentIndex];
					}

					if (updatedQueue.length === 0) {
						setEndOutcome(EndOutcome.SUCCEEDED);
					}

					return updatedQueue;
				});
			}, 100);
		},
		[answers, batteryLevel, decreaseBattery, increaseBattery, totalQuestions],
	);

	// Custom hook for answer submission
	const { submitAnswer, checkAnswer } = useTimeGameQuestion({
		currentQuestion,
		onAnswerSubmitted: handleAnswerSubmitted,
	});

	// Handle game start
	const handleStart = useCallback(() => {
		setHasGameStarted(true);
		transitionToGame();
	}, [transitionToGame]);

	const handleClose = useCallback(
		(finishEvent?: GameFinishEvent) => {
			onClose(finishEvent ? { finishEvent } : undefined);
		},
		[onClose],
	);

	// Handle back navigation
	const handleBack = useCallback(() => {
		handleClose(
			hasGameStarted
				? {
						gameOutcome: EndOutcome.ABANDONED,
						score: correctAnswersCount,
						data: {
							answeredQuestions: answers.length,
						},
				  }
				: undefined,
		);
	}, [answers.length, correctAnswersCount, handleClose, hasGameStarted]);

	// Handle game replay
	const handleReplay = useCallback(() => {
		const nextGameData = levelProgress?.user_learning_path_section;

		if (nextGameData) {
			onReplay?.(nextGameData);
		}
	}, [levelProgress, onReplay]);

	const handleTimeChange = useCallback((hours: number, minutes: number) => {
		setSelectedTime({ hours, minutes });
	}, []);

	const handleQuestionReady = useCallback(() => {
		questionStartedAtRef.current = Date.now();
	}, []);

	// Wrapper for checkAnswer that uses current selectedTime
	const handleCheckAnswer = useCallback(() => {
		const isAnalogAnswer = !needsDigitalAnswer;
		const isAnalogQuestion = (currentQuestion?.type || '').includes(TimeQuestionType.AnalogToDigital);
		return checkAnswer(selectedTime, isAnalogAnswer, isAnalogQuestion);
	}, [selectedTime, needsDigitalAnswer, currentQuestion, checkAnswer]);

	// Handle answer submission via console
	const handleConsoleLaunch = useCallback(() => {
		if (!currentQuestion || !hasGameStarted || questionQueue.length === 0) {
			return;
		}

		const currentIndex = questionQueue[0];
		const attemptCount = questionAttempts[currentIndex] || 0;
		const isAnalogAnswer = !needsDigitalAnswer;
		const isAnalogQuestion = (currentQuestion.type || '').includes('AnalogToDigital');
		const isSecondAttempt = attemptCount > 0;

		// Check if answer is correct
		const isCorrect = checkAnswer(selectedTime, isAnalogAnswer, isAnalogQuestion);
		const answers = currentQuestion.answers ?? [];
		const correctAnswer = answers.find((answer) => answer?.is_correct) ?? answers[0];
		const parsedExpected = parseTimeString(correctAnswer?.answer_display);

		// Complete the answer flow if: correct on first attempt OR any second attempt.
		const shouldCompleteAnswer = isCorrect || isSecondAttempt;
		const batteryDelta = isCorrect ? 1 : -1;
		const projectedBatteryLevel = Math.min(
			Math.max(batteryLevel + batteryDelta, MIN_BATTERY_LEVEL),
			MAX_BATTERY_LEVEL,
		);
		const willFinishOutcome =
			projectedBatteryLevel <= MIN_BATTERY_LEVEL
				? EndOutcome.LOST
				: shouldCompleteAnswer && questionQueue.length === 1
				? EndOutcome.SUCCEEDED
				: null;
		const shouldFinish = willFinishOutcome != null;
		const finishEvent = shouldCompleteAnswer && shouldFinish ? { gameOutcome: willFinishOutcome } : undefined;

		// Update attempt count for first wrong attempts
		if (!shouldCompleteAnswer) {
			setQuestionAttempts((prev) => ({
				...prev,
				[currentIndex]: attemptCount + 1,
			}));
		}

		const answerTimeInSeconds = getElapsedSeconds(questionStartedAtRef.current);
		const submissionTime: SelectedTime = {
			hours: isAnalogAnswer && parsedExpected ? parsedExpected.hours : selectedTime.hours,
			minutes: selectedTime.minutes,
		};

		onAnswer?.({
			gameType: GAME_TYPES.TIME_GAME,
			question: currentQuestion,
			questionIndex: currentIndex,
			isCorrect,
			isSecondAttempt,
			answerId: correctAnswer?.id,
			answerTexts: [formatSelectedTime(submissionTime)],
			answerTimeInSeconds,
			data: {
				attemptCount: attemptCount + 1,
				isAnalogAnswer,
				isAnalogQuestion,
				selectedTime,
			},
			finishEvent,
		});

		submitAnswer(selectedTime, isAnalogAnswer, isSecondAttempt);
	}, [
		currentQuestion,
		hasGameStarted,
		needsDigitalAnswer,
		selectedTime,
		submitAnswer,
		checkAnswer,
		questionQueue,
		questionAttempts,
		batteryLevel,
		onAnswer,
	]);

	// Show end screen
	if (endOutcome !== null) {
		const wrongAnswersCount = answers.filter((a) => !a).length;
		const currentLevel = levelProgress?.current_level;

		return (
			<EndScreen
				outcome={endOutcome}
				showMilepost={Boolean(levelProgress)}
				onClose={() => handleClose()}
				onReplay={handleReplay}
				progressStarsEarned={starProgressData?.progressStarsEarned}
				totalStars={starProgressData?.totalStars}
				earnedStars={starProgressData?.earnedStars}
				previousEarned={starProgressData?.previousEarned}
				levelImage={currentLevel?.level_image}
				levelName={currentLevel?.level_name}
				levelTitle={currentLevel?.level_title}
				levelCompleted={levelProgress?.level_completed}
				totalQuestions={totalQuestions}
				wrongAnswers={wrongAnswersCount}
			/>
		);
	}

	// Main game screen
	if (!currentQuestion) {
		return null;
	}

	const shouldShowStartPanel = !hasGameStarted && shouldRenderStartPanel;

	return (
		<div className="time-game-stage">
			{shouldShowStartPanel ? (
				<div className="time-game-stage__panel time-game-stage__panel--start" ref={startPanelRef}>
					<StartScreen onStart={handleStart} onBack={() => handleClose()} />
				</div>
			) : null}

			<div className="time-game-stage__panel time-game-stage__panel--game" ref={gamePanelRef}>
				<GameScreen
					key={gameScreenResizeReloadKey}
					currentQuestion={currentQuestion}
					currentQuestionIndex={currentQuestionQueueIndex ?? 0}
					currentAlienPosition={currentAlienPosition}
					totalQuestions={totalQuestions}
					correctAnswersCount={correctAnswersCount}
					questionOrder={questionQueue}
					onLaunch={handleConsoleLaunch}
					onTimeChange={handleTimeChange}
					checkAnswer={handleCheckAnswer}
					onBack={handleBack}
					initialHours={selectedTime.hours}
					initialMinutes={selectedTime.minutes}
					resetKey={currentQuestion.id}
					batteryLevel={batteryLevel}
					hasGameStarted={hasGameStarted}
					onQuestionReady={handleQuestionReady}
				/>
			</div>
		</div>
	);
};

const TimeGame = (props: TimeGameProps) => {
	const { audioOn, onAudioChange, ...restProps } = props;

	return (
		<GameStyleBoundary>
			<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
				<TimeGameContent {...restProps} />
			</GameAudioProvider>
		</GameStyleBoundary>
	);
};

export default TimeGame;
